import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Building2, Factory, HardHat, Handshake, ArrowUpRight } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";

const KIND_META = {
  office: { icon: Building2, en: "Office", ar: "مكتب" },
  plant: { icon: Factory, en: "Plant", ar: "مصنع" },
  project: { icon: HardHat, en: "Project", ar: "مشروع" },
  venture: { icon: Handshake, en: "Joint Venture", ar: "مشروع مشترك" },
};

const GLYPH = {
  office: '<path d="M3 21h18M5 21V5a1 1 0 011-1h7a1 1 0 011 1v16M17 21v-9h3v9M8 7h2M8 11h2M8 15h2"/>',
  plant: '<path d="M2 20h20M4 20V9l5 3V9l5 3V9l5 3v8M9 16h1M14 16h1"/>',
  project: '<path d="M4 20h16M6 20V9l6-5 6 5v11M10 20v-5h4v5"/>',
  venture: '<path d="M3 12h4l2-3 3 6 2-4 2 2h5"/>',
};

const FILTERS = [
  { id: "all", en: "All locations", ar: "جميع المواقع" },
  { id: "uae", en: "United Arab Emirates", ar: "الإمارات" },
  { id: "egypt", en: "Egypt", ar: "مصر" },
];

const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas";
const TILES = {
  dark: {
    base: `${ESRI}/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`,
    labels: `${ESRI}/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}`,
  },
  light: {
    base: `${ESRI}/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`,
    labels: `${ESRI}/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}`,
  },
};
const ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com">Esri</a> &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors';

const pinIcon = (kind, active) =>
  L.divIcon({
    className: "qalaa-pin-wrap",
    html: `<span class="qalaa-pin ${active ? "is-active" : ""}">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
           stroke-linecap="round" stroke-linejoin="round">${GLYPH[kind] || GLYPH.project}</svg>
    </span>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -18],
  });

function MapFocus({ target, bounds }) {
  const map = useMap();

  // the container is measured before the section has laid out, so Leaflet can
  // start with a zero-size viewport and load no tiles at all
  useEffect(() => {
    const fix = () => map.invalidateSize();
    const t = setTimeout(fix, 250);
    window.addEventListener("resize", fix);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", fix);
    };
  }, [map]);

  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 12, { duration: 1.1 });
    else if (bounds) {
      const t = setTimeout(() => map.flyToBounds(bounds, { padding: [60, 60], duration: 1.1 }), 300);
      return () => clearTimeout(t);
    }
  }, [target, bounds, map]);
  return null;
}

export const PresenceMap = () => {
  const { L: T, lang, dir } = useLang();
  const site = useSite();
  const locations = site?.locations || [];
  const isLight = site?.settings?.theme === "desert-minimalist";

  const [filter, setFilter] = useState("all");
  const [activeId, setActiveId] = useState(null);
  const markerRefs = useRef({});

  const shown = useMemo(
    () => locations.filter((l) => filter === "all" || l.country === filter),
    [locations, filter],
  );

  const bounds = useMemo(() => {
    if (!shown.length) return null;
    return L.latLngBounds(shown.map((l) => [l.lat, l.lng]));
  }, [shown]);

  const active = shown.find((l) => l.id === activeId) || null;

  const select = (loc) => {
    setActiveId(loc.id);
    const marker = markerRefs.current[loc.id];
    if (marker) setTimeout(() => marker.openPopup(), 650);
  };

  useEffect(() => {
    setActiveId(null);
  }, [filter]);

  if (!locations.length) return null;

  return (
    <div data-testid="presence-map">
      <div className="mb-6 flex flex-wrap gap-2" data-testid="presence-map-filters">
        {FILTERS.map((f) => {
          const count = f.id === "all" ? locations.length : locations.filter((l) => l.country === f.id).length;
          return (
            <button
              key={f.id}
              type="button"
              data-testid={`map-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                filter === f.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary/60 hover:text-primary"
              }`}
            >
              {lang === "ar" ? f.ar : f.en}
              <span className="ms-2 opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div
          className="qalaa-map overflow-hidden border border-border"
          style={{ borderRadius: "var(--radius)" }}
          data-testid="presence-map-canvas"
        >
          <MapContainer
            center={[27.2, 42.0]}
            zoom={5}
            scrollWheelZoom={false}
            style={{ height: "100%", width: "100%", background: "transparent" }}
          >
            <TileLayer url={(isLight ? TILES.light : TILES.dark).base} attribution={ATTRIBUTION} />
            <TileLayer url={(isLight ? TILES.light : TILES.dark).labels} />
            <MapFocus target={active} bounds={bounds} />
            {shown.map((loc) => (
              <Marker
                key={loc.id}
                position={[loc.lat, loc.lng]}
                icon={pinIcon(loc.kind, activeId === loc.id)}
                ref={(m) => {
                  if (m) markerRefs.current[loc.id] = m;
                }}
                eventHandlers={{ click: () => setActiveId(loc.id) }}
              >
                <Popup>
                  <div dir={dir} className="qalaa-popup">
                    <p className="qalaa-popup-kind">
                      {lang === "ar" ? KIND_META[loc.kind]?.ar : KIND_META[loc.kind]?.en}
                    </p>
                    <p className="qalaa-popup-title">{T(loc.name)}</p>
                    <p className="qalaa-popup-city">{T(loc.city)}</p>
                    {T(loc.summary) ? <p className="qalaa-popup-body">{T(loc.summary)}</p> : null}
                    {loc.link ? (
                      <Link to={loc.link} className="qalaa-popup-link" data-testid={`map-popup-link-${loc.id}`}>
                        {lang === "ar" ? "اعرف المزيد" : "Learn more"} <ArrowUpRight size={12} />
                      </Link>
                    ) : null}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <ul className="space-y-3" data-testid="presence-map-list">
          {shown.map((loc) => {
            const Icon = KIND_META[loc.kind]?.icon || HardHat;
            const isActive = activeId === loc.id;
            return (
              <li key={loc.id}>
                <button
                  type="button"
                  onClick={() => select(loc)}
                  data-testid={`map-list-${loc.id}`}
                  className={`flex w-full items-start gap-4 border p-4 text-start transition-colors ${
                    isActive ? "border-primary bg-primary/5" : "border-border bg-card/40 hover:border-primary/50"
                  }`}
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <span
                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                      isActive ? "bg-primary text-primary-foreground" : "border border-border text-primary"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      {lang === "ar" ? KIND_META[loc.kind]?.ar : KIND_META[loc.kind]?.en}
                    </span>
                    <span className="mt-1 block font-head text-lg leading-tight">{T(loc.name)}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{T(loc.city)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PresenceMap;
